import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Portal from './Portal';
import isMobileAppRuntime from 'utils/isMobileAppRuntime';
import mobileAppIcon from './flc-mobile-icon.png';
import { localizeMobileLoadingText, resolveMobileLoadingLocale, MOBILE_LOADING_STRING_IDS } from './mobileLoadingMessages';
import getTranslateFunc from 'utils/getTranslateFunc';
import { logOfflineDiagnosticEvent } from 'mobile-modules/offline/assets';
import { getCachedPlayBootstrap } from 'mobile-modules/offline/cache';

const DEFAULT_PHASES = [
  { max: 32, label: 'Loading data...' },
  { max: 86, label: 'Preparing learning content...' },
  { max: 96, label: 'Almost ready...' },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getUploadProgress = (uploading) => {
  if (!uploading || !Number(uploading.total)) return null;
  return clamp(Math.round((Number(uploading.loaded || 0) / Number(uploading.total)) * 100), 0, 100);
};

const getPhaseLabel = (progress, explicitLabel) => {
  if (explicitLabel) return explicitLabel;
  return DEFAULT_PHASES.find((phase) => progress <= phase.max)?.label || 'Almost ready...';
};

// The Play bootstrap is the authoritative source for the Solution UI language.
// The Login language is independent and must not localize the early Play overlay.
const getBootstrapSolutionLocale = (bootstrap) => {
  if (!bootstrap) return '';
  const onboarding = bootstrap.learner?.customContentOnboarding || {};
  const selectedId = String(bootstrap.learner?.settings?.selectedSolutionId || '');
  const authoredSolution = selectedId
    ? (bootstrap.product?.solutions || []).find((item) => String(item?._id || '') === selectedId)
    : null;
  return String(
    bootstrap.solutionLocale?.code
    || authoredSolution?.locale
    || bootstrap.solution?.locale
    || onboarding.explanationLanguage
    || '',
  ).trim().toLowerCase().split(/[-_]/)[0];
};

const getLearnerId = (learner) => String(
  (learner && typeof learner === 'object' ? learner._id || learner.id : learner) || '',
);

const MobileAppLoadingOverlay = ({ uploading, progress, label, detail, phaseSource }) => {
  // Redux values are read directly; no newly created objects from useSelector.
  const play = useSelector((state) => state.flinkPlay);
  const loginLocale = useSelector((state) => state.status?.lang?.code || '');
  const activeLearnerId = useSelector((state) => getLearnerId(state.auth?.learner));
  const strings = useSelector((state) => state.common?.strings);
  const [cachedLocale, setCachedLocale] = useState('');

  useEffect(() => {
    if (!isMobileAppRuntime() || !activeLearnerId) {
      setCachedLocale('');
      return undefined;
    }
    let active = true;
    getCachedPlayBootstrap().then((bootstrap) => {
      // A previous user's cached Placement/Custom Content must never choose
      // the current user's loading language.
      if (!active || getLearnerId(bootstrap?.learner) !== activeLearnerId) return;
      setCachedLocale(getBootstrapSolutionLocale(bootstrap));
    }).catch(() => undefined);
    return () => { active = false; };
  }, [activeLearnerId]);

  const locale = useMemo(() => {
    const resolved = resolveMobileLoadingLocale({
      flinkPlay: play,
      status: { lang: { code: loginLocale } },
    });
    if (!activeLearnerId || (resolved.source !== 'status.lang (before session)' && resolved.source !== 'default')) {
      return resolved;
    }
    const uiLocale = getBootstrapSolutionLocale(play) || cachedLocale;
    return uiLocale
      ? { code: uiLocale, source: 'solution.bootstrap' }
      : { code: '', source: 'awaiting-solution-locale' };
  }, [play, loginLocale, cachedLocale, activeLearnerId]);
  const solutionLocale = locale.code;
  const lastDiagnostic = useRef('');
  const catalogTranslate = useMemo(() => getTranslateFunc(strings || [], solutionLocale), [strings, solutionLocale]);
  const translate = (value) => {
    if (typeof value !== 'string') return value;
    const id = MOBILE_LOADING_STRING_IDS[value];
    if (id && solutionLocale !== 'en') {
      const result = catalogTranslate(id, value, false, true);
      if (typeof result === 'string' && result.trim() && result !== value) return result;
    }
    return localizeMobileLoadingText(value, solutionLocale);
  };
  const uploadProgress = useMemo(() => getUploadProgress(uploading), [uploading]);
  const hasUploadProgress = uploadProgress !== null;
  const explicitProgress = Number.isFinite(progress)
    ? clamp(Number(progress), 0, 100)
    : null;
  const hasRealProgress = hasUploadProgress || explicitProgress !== null;
  const initialProgress = hasUploadProgress
    ? uploadProgress
    : explicitProgress;
  const [displayProgress, setDisplayProgress] = useState(initialProgress);

  useEffect(() => {
    // Never manufacture progress for network/preparation phases. If a caller has
    // no measurable percentage yet, the overlay stays indeterminate until that
    // phase reports real file/byte progress.
    if (!hasRealProgress) {
      setDisplayProgress(null);
      return undefined;
    }

    setDisplayProgress(initialProgress);
    return undefined;
  }, [hasRealProgress, initialProgress]);

  const originalStatusLabel = uploading
    ? 'Uploading data...'
    : (label || (displayProgress !== null ? getPhaseLabel(displayProgress, label) : 'Loading...'));

  // If there is no verified Solution locale yet, keep the native progress
  // visible without briefly showing English labels from the Login fallback.
  const statusLabel = solutionLocale ? translate(originalStatusLabel) : '';
  const localizedDetail = solutionLocale ? translate(detail) : '';

  // Diagnostic only: record what the mobile user actually sees and where the
  // locale came from. Do not alter the chosen locale, text or progress.
  useEffect(() => {
    if (!isMobileAppRuntime()) return;
    const signature = JSON.stringify([phaseSource, originalStatusLabel, detail, statusLabel, localizedDetail, solutionLocale]);
    if (lastDiagnostic.current === signature) return;
    lastDiagnostic.current = signature;
    const record = {
      screen: 'MobileAppLoadingOverlay',
      phaseSource: phaseSource || 'spinner-or-default',
      originalLabel: originalStatusLabel,
      renderedLabel: statusLabel,
      originalDetail: detail || '',
      renderedDetail: localizedDetail || '',
      appliedLocale: solutionLocale || '',
      solutionLocaleCode: play?.solutionLocale?.code || '',
      statusLocaleCode: loginLocale,
      storedLocaleCode: window.localStorage?.getItem('localeCode') || '',
      localeSource: locale.source,
      sessionInstructionLocale: play?.learner?.customContentOnboarding?.instructionLanguage || play?.mobileBootstrap?.offlineAssessment?.instructionLanguage || '',
      sessionExplanationLocale: play?.learner?.customContentOnboarding?.explanationLanguage || play?.mobileBootstrap?.offlineAssessment?.explanationLanguage || '',
      labelTranslated: originalStatusLabel !== statusLabel,
      detailTranslated: Boolean(detail && detail !== localizedDetail),
    };
    console.info('[FLC][loader-locale]', record);
    logOfflineDiagnosticEvent('mobile-loader-locale', record);
  }, [phaseSource, originalStatusLabel, detail, statusLabel, localizedDetail, solutionLocale, locale.source, play, loginLocale]);

  if (!isMobileAppRuntime()) return null;

  return (
    <Portal>
      <Container role="status" aria-live="polite" aria-label={displayProgress !== null ? `${statusLabel} ${displayProgress}%` : statusLabel}>
        <Backdrop />
        <Panel>
          <BrandMark aria-hidden="true"><img src={mobileAppIcon} alt="" /></BrandMark>
          <Status>{statusLabel || '\u00a0'}</Status>
          {localizedDetail && <Detail>{localizedDetail}</Detail>}
          <ProgressRow>
            <Track>
              {displayProgress !== null
                ? <Fill style={{ width: `${displayProgress}%` }} />
                : <IndeterminateFill />}
            </Track>
            {displayProgress !== null && <Percent>{displayProgress}%</Percent>}
          </ProgressRow>
        </Panel>
      </Container>
    </Portal>
  );
};

MobileAppLoadingOverlay.propTypes = {
  uploading: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  progress: PropTypes.number,
  label: PropTypes.string,
  detail: PropTypes.string,
  phaseSource: PropTypes.string,
};

export default MobileAppLoadingOverlay;

const Container = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: max(18px, env(safe-area-inset-top)) max(22px, env(safe-area-inset-right)) max(18px, env(safe-area-inset-bottom)) max(22px, env(safe-area-inset-left));
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(239, 246, 255, .94);
  backdrop-filter: blur(8px);
`;

const Panel = styled.div`
  position: relative;
  z-index: 1;
  width: min(58vw, 540px);
  min-width: 320px;
  padding: clamp(22px, 4.5vh, 38px) clamp(28px, 5vw, 54px);
  border: 1px solid rgba(111, 145, 198, .24);
  border-radius: 26px;
  box-sizing: border-box;
  background: rgba(255, 255, 255, .97);
  box-shadow: 0 18px 50px rgba(30, 61, 115, .16);
  text-align: center;
`;

const BrandMark = styled.div`
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  margin: 0 auto 16px;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 8px 22px rgba(30, 61, 115, .14);

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
  }
`;

const Status = styled.div`
  color: #17345f;
  font-size: clamp(18px, 4vh, 25px);
  line-height: 1.15;
  font-weight: 800;
`;

const Detail = styled.div`
  margin-top: 7px;
  color: #71829e;
  font-size: clamp(11px, 2.5vh, 14px);
  line-height: 1.35;
`;

const ProgressRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  margin-top: 22px;
`;

const Track = styled.div`
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: #e5edf8;
`;

const IndeterminateFill = styled.div`
  width: 34%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #8b5cf6, #3b82f6 58%, #22c7a8);
  animation: flc-loader-indeterminate 1.15s ease-in-out infinite;

  @keyframes flc-loader-indeterminate {
    0% { transform: translateX(-110%); }
    100% { transform: translateX(300%); }
  }
`;

const Fill = styled.div`
  height: 100%;
  min-width: 4px;
  border-radius: inherit;
  background: linear-gradient(90deg, #8b5cf6, #3b82f6 58%, #22c7a8);
  transition: width 260ms ease;
`;

const Percent = styled.div`
  min-width: 42px;
  color: #31547d;
  font-size: 14px;
  font-weight: 800;
  text-align: right;
`;
